import Matter from 'matter-js';
import { ballRadii, droppableRankCount, mergeScore } from '@photo-merge/shared';
import {
  DENSITY,
  DROP_COOLDOWN_MS,
  DROP_SPIN,
  DROP_Y,
  FILL_LINE_Y,
  FRICTION,
  FRICTION_AIR,
  FRICTION_STATIC,
  GRAVITY_Y,
  MAX_TICKS_PER_FRAME,
  MERGE_ATTRACT,
  MERGE_CHAIN_DELAY_MS,
  MERGE_FX_MS,
  MERGE_POP,
  OVERFLOW_GRACE_MS,
  RESTITUTION,
  SETTLED_SPEED,
  SPAWN_BIAS_DROPS,
  SPAWN_BIAS_START,
  SPAWN_IMMUNITY_MS,
  TICK_MS,
  WALL_THICKNESS,
  WORLD_WIDTH,
} from './constants';

/** Extra fields we hang off each Matter body. */
interface BallData {
  rank: number;
  radius: number;
  bornAt: number;
  /** When this ball first came to rest above the fill line, else null. */
  overLineSince: number | null;
  /** Set the instant a body is claimed by a merge, so it can't merge twice. */
  consumed: boolean;
  /** Earliest time this ball may merge. Staggers chain reactions so they're visible. */
  mergeReadyAt: number;
  /** Drives the spawn "pop in" scale animation. */
  spawnFx: number;
}

type Ball = Matter.Body & { ball: BallData };

/** A merge committed on contact, waiting for its scheduled moment. */
interface PendingMerge {
  a: Ball;
  b: Ball;
  dueAt: number;
}

export interface MergeFx {
  x: number;
  y: number;
  radius: number;
  rank: number;
  /** 0 → 1 over MERGE_FX_MS. */
  progress: number;
  /** True when two top-rank balls annihilated. */
  climax: boolean;
}

export type GamePhase = 'ready' | 'playing' | 'over';

export interface GameSnapshot {
  phase: GamePhase;
  score: number;
  /** Rank of the ball currently held at the top, or null during cooldown. */
  heldRank: number | null;
  heldX: number;
  nextRank: number;
  /** 0 → 1 as the overflow grace period runs out; drives the danger warning. */
  dangerProgress: number;
}

export interface EngineOptions {
  ballCount: number;
  worldHeight: number;
  onScore?: (total: number) => void;
  onGameOver?: (score: number) => void;
  /** Injectable so tests can force a known sequence of ranks. Defaults to Math.random. */
  random?: () => number;
}

export class GameEngine {
  readonly worldWidth = WORLD_WIDTH;
  readonly worldHeight: number;
  readonly ballCount: number;
  readonly radii: number[];

  private engine: Matter.Engine;
  private runnerAccumulator = 0;
  private phase: GamePhase = 'ready';
  private score = 0;
  private heldRank: number;
  private nextRank: number;
  private heldX: number;
  private cooldownUntil = 0;
  private now = 0;
  private drops = 0;
  private mergeQueue: PendingMerge[] = [];
  private effects: MergeFx[] = [];
  private opts: EngineOptions;
  private random: () => number;

  constructor(opts: EngineOptions) {
    this.opts = opts;
    this.random = opts.random ?? Math.random;
    this.ballCount = opts.ballCount;
    this.worldHeight = opts.worldHeight;
    this.radii = ballRadii(opts.ballCount, WORLD_WIDTH);

    this.engine = Matter.Engine.create({
      gravity: { x: 0, y: GRAVITY_Y, scale: 0.001 },
      // Extra position iterations keep tall stacks from sinking into each other.
      positionIterations: 8,
      velocityIterations: 6,
    });

    this.heldRank = this.rollRank();
    this.nextRank = this.rollRank();
    this.heldX = WORLD_WIDTH / 2;

    this.buildWalls();

    // Both events: collisionStart alone misses pairs that are already resting in
    // contact when they become mergeable — nothing would ever re-trigger them.
    Matter.Events.on(this.engine, 'collisionStart', (e) => this.onCollisions(e));
    Matter.Events.on(this.engine, 'collisionActive', (e) => this.onCollisions(e));
  }

  // --- Public API ---

  get snapshot(): GameSnapshot {
    return {
      phase: this.phase,
      score: this.score,
      heldRank: this.canDrop ? this.heldRank : null,
      heldX: this.heldX,
      nextRank: this.nextRank,
      dangerProgress: this.dangerProgress(),
    };
  }

  get bodies(): Matter.Body[] {
    return Matter.Composite.allBodies(this.engine.world).filter((b) => !b.isStatic);
  }

  get activeEffects(): MergeFx[] {
    return this.effects;
  }

  get radiusOf(): (rank: number) => number {
    return (rank) => this.radii[rank - 1] ?? this.radii[0]!;
  }

  private get canDrop(): boolean {
    return this.phase !== 'over' && this.now >= this.cooldownUntil;
  }

  /** Moves the held ball. `x` is in world units and gets clamped to the walls. */
  aim(x: number): void {
    const r = this.radiusOf(this.heldRank);
    this.heldX = Math.max(r + 2, Math.min(WORLD_WIDTH - r - 2, x));
  }

  drop(): boolean {
    if (!this.canDrop) return false;
    if (this.phase === 'ready') this.phase = 'playing';

    const rank = this.heldRank;
    const body = this.makeBall(rank, this.heldX, DROP_Y);
    // A tiny random spin stops perfectly-stacked drops from behaving identically.
    Matter.Body.setAngularVelocity(body, (this.random() * 2 - 1) * DROP_SPIN);
    Matter.Composite.add(this.engine.world, body);

    this.heldRank = this.nextRank;
    this.nextRank = this.rollRank();
    this.cooldownUntil = this.now + DROP_COOLDOWN_MS;
    this.drops++;
    return true;
  }

  /** Advances the simulation. `elapsed` is real milliseconds since the last call. */
  update(elapsed: number): void {
    this.now += elapsed;

    if (this.phase === 'over') {
      this.decayEffects(elapsed);
      return;
    }

    this.runnerAccumulator += elapsed;
    let ticks = 0;
    while (this.runnerAccumulator >= TICK_MS && ticks < MAX_TICKS_PER_FRAME) {
      this.drawPairsTogether();
      Matter.Engine.update(this.engine, TICK_MS);
      // Merges are resolved *after* the step, never inside the collision callback:
      // mutating the world mid-solve is how one ball ends up merging with two
      // neighbours in the same tick and duplicating itself.
      this.resolveMerges();
      this.runnerAccumulator -= TICK_MS;
      ticks++;
    }
    // If we blew the budget, drop the backlog rather than accumulating debt forever.
    if (ticks === MAX_TICKS_PER_FRAME) this.runnerAccumulator = 0;

    this.decayEffects(elapsed);
    this.advanceSpawnFx(elapsed);
    this.checkOverflow(elapsed);
  }

  destroy(): void {
    Matter.Events.off(this.engine, 'collisionStart');
    Matter.Events.off(this.engine, 'collisionActive');
    Matter.Composite.clear(this.engine.world, false, true);
    Matter.Engine.clear(this.engine);
  }

  // --- Internals ---

  private rollRank(): number {
    const top = droppableRankCount(this.ballCount);
    const pick = () => Math.min(top - 1, Math.floor(this.random() * top));

    // Take the smaller of two draws some of the time — a cheap way to lean small
    // without ever excluding a rank. The lean fades as the game gets going, so an
    // opening board isn't handed balls it has nothing to merge with.
    const lean = SPAWN_BIAS_START * Math.max(0, 1 - this.drops / SPAWN_BIAS_DROPS);
    const first = pick();
    if (this.random() >= lean) return first + 1;
    return Math.min(first, pick()) + 1;
  }

  private buildWalls(): void {
    const t = WALL_THICKNESS;
    const w = WORLD_WIDTH;
    const h = this.worldHeight;
    const opts: Matter.IChamferableBodyDefinition = {
      isStatic: true,
      friction: FRICTION,
      restitution: RESTITUTION,
    };
    // Walls sit just outside the visible board and extend well above it, so a ball
    // bouncing up near the rim can't escape sideways.
    Matter.Composite.add(this.engine.world, [
      Matter.Bodies.rectangle(-t / 2, h / 2 - t, t, h * 2 + t * 2, opts),
      Matter.Bodies.rectangle(w + t / 2, h / 2 - t, t, h * 2 + t * 2, opts),
      Matter.Bodies.rectangle(w / 2, h + t / 2, w + t * 2, t, opts),
    ]);
  }

  private makeBall(rank: number, x: number, y: number, mergeReadyAt = 0): Ball {
    const radius = this.radiusOf(rank);
    const body = Matter.Bodies.circle(x, y, radius, {
      restitution: RESTITUTION,
      friction: FRICTION,
      frictionStatic: FRICTION_STATIC,
      frictionAir: FRICTION_AIR,
      density: DENSITY,
    }) as Ball;
    body.ball = {
      rank,
      radius,
      bornAt: this.now,
      overLineSince: null,
      consumed: false,
      mergeReadyAt,
      spawnFx: 0,
    };
    return body;
  }

  private onCollisions(event: Matter.IEventCollision<Matter.Engine>): void {
    for (const pair of event.pairs) {
      const a = pair.bodyA as Ball;
      const b = pair.bodyB as Ball;
      if (!a.ball || !b.ball) continue;
      if (a.ball.rank !== b.ball.rank) continue;
      if (a.ball.consumed || b.ball.consumed) continue;

      // Claim both the instant they touch. Two reasons: Matter can report the same
      // body in several pairs in one tick, and committing here means a pair that has
      // to wait out the chain delay can't be lost by bouncing apart in the meantime.
      a.ball.consumed = true;
      b.ball.consumed = true;
      this.mergeQueue.push({
        a,
        b,
        dueAt: Math.max(this.now, a.ball.mergeReadyAt, b.ball.mergeReadyAt),
      });
    }
  }

  /**
   * Holds committed pairs together while they wait. Without this the merge is still
   * correct, but the two balls visibly drift apart and then snap together, which reads
   * as a glitch rather than as a chain.
   */
  private drawPairsTogether(): void {
    for (const { a, b } of this.mergeQueue) {
      const dx = b.position.x - a.position.x;
      const dy = b.position.y - a.position.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 1e-3) continue;
      const ux = dx / distance;
      const uy = dy / distance;
      Matter.Body.applyForce(a, a.position, {
        x: ux * MERGE_ATTRACT * a.mass,
        y: uy * MERGE_ATTRACT * a.mass,
      });
      Matter.Body.applyForce(b, b.position, {
        x: -ux * MERGE_ATTRACT * b.mass,
        y: -uy * MERGE_ATTRACT * b.mass,
      });
    }
  }

  private resolveMerges(): void {
    if (!this.mergeQueue.length) return;

    const due: PendingMerge[] = [];
    const waiting: PendingMerge[] = [];
    for (const pending of this.mergeQueue) {
      (this.now >= pending.dueAt ? due : waiting).push(pending);
    }
    this.mergeQueue = waiting;
    if (!due.length) return;

    for (const { a, b } of due) {
      const rank = a.ball.rank;
      const x = (a.position.x + b.position.x) / 2;
      const y = (a.position.y + b.position.y) / 2;
      const isTop = rank >= this.ballCount;

      Matter.Composite.remove(this.engine.world, a);
      Matter.Composite.remove(this.engine.world, b);

      this.score += mergeScore(rank);
      this.opts.onScore?.(this.score);

      this.effects.push({
        x,
        y,
        radius: isTop ? a.ball.radius * 1.6 : this.radiusOf(rank + 1),
        rank,
        progress: 0,
        climax: isTop,
      });

      // Two of the largest annihilate, exactly like a pair of watermelons.
      if (isTop) continue;

      const merged = this.makeBall(rank + 1, x, y, this.now + MERGE_CHAIN_DELAY_MS);
      // Inherit momentum so the merge flows out of the collision instead of
      // teleporting a new ball into a dead stop.
      Matter.Body.setVelocity(merged, {
        x: (a.velocity.x + b.velocity.x) / 2,
        y: (a.velocity.y + b.velocity.y) / 2,
      });
      Matter.Composite.add(this.engine.world, merged);
      Matter.Body.applyForce(merged, merged.position, {
        x: 0,
        y: -MERGE_POP * merged.mass,
      });
    }
  }

  private advanceSpawnFx(elapsed: number): void {
    for (const body of this.bodies) {
      const ball = (body as Ball).ball;
      if (ball && ball.spawnFx < 1) {
        ball.spawnFx = Math.min(1, ball.spawnFx + elapsed / 160);
      }
    }
  }

  private decayEffects(elapsed: number): void {
    for (const fx of this.effects) fx.progress += elapsed / MERGE_FX_MS;
    this.effects = this.effects.filter((fx) => fx.progress < 1);
  }

  /**
   * Ends the game when a ball has *rested* above the fill line continuously for the
   * grace period. Balls in flight and freshly dropped balls are exempt, so ordinary
   * bouncing near the rim doesn't kill you.
   */
  private checkOverflow(elapsed: number): void {
    void elapsed;
    let worst = 0;

    for (const body of this.bodies) {
      const ball = (body as Ball).ball;
      if (!ball) continue;

      const speed = Math.hypot(body.velocity.x, body.velocity.y);
      // The ball's centre, not either edge. Its top edge triggers while most of the
      // ball is still legitimately below the line; its bottom edge lets a ball sit
      // almost entirely above the line without counting. The midpoint reads as
      // "this ball is over the line" to a player watching it.
      const above = body.position.y < FILL_LINE_Y;
      const settled = speed < SETTLED_SPEED;
      const grownUp = this.now - ball.bornAt > SPAWN_IMMUNITY_MS;

      if (above && settled && grownUp) {
        ball.overLineSince ??= this.now;
        worst = Math.max(worst, (this.now - ball.overLineSince) / OVERFLOW_GRACE_MS);
      } else {
        ball.overLineSince = null;
      }
    }

    if (worst >= 1 && this.phase === 'playing') {
      this.phase = 'over';
      this.opts.onGameOver?.(this.score);
    }
  }

  private dangerProgress(): number {
    let worst = 0;
    for (const body of this.bodies) {
      const ball = (body as Ball).ball;
      if (ball?.overLineSince != null) {
        worst = Math.max(worst, (this.now - ball.overLineSince) / OVERFLOW_GRACE_MS);
      }
    }
    return Math.min(1, worst);
  }
}

export type { Ball, BallData };
